import { Injectable } from '@angular/core';
import { AngularFirestore, Query } from '@angular/fire/firestore';
import { AngularFireAuth } from '@angular/fire/auth';
import { from, Observable, of, pipe } from 'rxjs';
import { map, mergeMap, switchMap, take } from 'rxjs/operators';

import { InviteUserModel } from '@app/interface';
import { SurveyQuestion, Survey } from '../interface/survey';
import { DefaultActivity } from '../interface/activity';
import { AuthUser, UserDetail } from '../interface/auth.user';
import { StudiesService } from './studies.service';
import { StudyEnroll } from '@app/interface/enrollInStudy.model';
import { UserGroupModel } from '@app/interface/userGroup.model';
import { query } from '@angular/animations';
import { FirebaseAuthService } from './firebaseauth.service';
import * as firebase from 'firebase';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  fireUserId: any;
  constructor(
    private store: AngularFirestore,
    private afAuth: AngularFireAuth,
    private studiesService: StudiesService,
    private authService: FirebaseAuthService
  ) {
    this.getAuthUser(() => {});
  }

  // Get auth user
  public getAuthUser(cb: any): void {
    this.afAuth.authState.subscribe(
      (authUser: AuthUser) => {
        if (authUser) {
          this.fireUserId = authUser.uid;
          cb();
        }
      },
      () => {
        this.fireUserId = null;
      }
    );
  }

  public getFcmTokens() {
    return this.store.collection<SurveyQuestion>('fcm-tokens').valueChanges();
  }

  // Survey questions
  public getSurveyQuestionUUID(surveyQuestionUUID: any) {
    localStorage.removeItem('surveyQuestionUUID');
    localStorage.setItem('surveyQuestionUUID', surveyQuestionUUID);
  }

  public addSurveyQuestion(surveyQuestion: SurveyQuestion, surveyUUID: string) {
    return this.authService
      .getUser()
      .pipe(
        mergeMap((user: UserDetail) => {
          if (!user) {
            return of(null);
          }
          surveyQuestion.createdAt = firebase.default.firestore.Timestamp.now();
          surveyQuestion.updatedAt = firebase.default.firestore.Timestamp.now();
          surveyQuestion.createdBy = user.role;
          surveyQuestion.createdByuserId = user.uuid;
          return this.store
            .collection<SurveyQuestion>('surveys')
            .doc(surveyUUID)
            .collection('questions')
            .doc(surveyQuestion.uuid)
            .set(surveyQuestion, { merge: true });
        })
      )
      .toPromise();
  }

  public getSurveyQuestions(surveyUUID: string) {
    return this.store.collection<SurveyQuestion>('surveys').doc(surveyUUID).collection('questions').valueChanges();
  }

  public deleteSurveyQuestion(questionUUID: string, surveyUUID: string) {
    return this.store
      .collection('surveys')
      .doc(surveyUUID)
      .collection<SurveyQuestion>('questions')
      .doc(questionUUID)
      .delete();
  }

  public getOneSurveyQuestion() {
    return this.store
      .collection('surveys')
      .doc(localStorage.getItem('surveyUUID'))
      .collection('questions')
      .doc(localStorage.getItem('surveyQuestionUUID'))
      .valueChanges();
  }

  // Surveys
  public addSurvey(survey: Survey) {
    return this.store.collection<Survey>('surveys').doc(survey.uuid).set(survey);
  }

  public cloneSurvey(survey: Survey) {
    return this.store.collection<Survey>('surveys').doc(survey.uuid).set(survey);
  }

  public editSurvey(survey: Survey) {
    return this.store.collection<Survey>('surveys').doc(localStorage.getItem('surveyUUID')).update(survey);
  }

  public getOneSurvey() {
    return this.store.collection<Survey>('surveys').doc(localStorage.getItem('surveyUUID')).valueChanges();
  }

  public getSurveys() {
    return this.store.collection<SurveyQuestion>('surveys').valueChanges();
  }

  public getAdminRoles(studyId: string) {
    return this.store
      .collection('admin-roles', (ref) => {
        let query: Query = ref;
        return query.where('studyId', '==', studyId);
      })
      .valueChanges()
      .pipe(take(1));
  }

  public getSchedules(studyId: string) {
    return this.store
      .collection('schedules', (ref) => {
        let query: Query = ref;
        return query.where('studyId', '==', studyId);
      })
      .valueChanges();
  }

  public addSchedules(schedule: any) {
    return this.authService.getUser().pipe(
      switchMap((user: UserDetail) => {
        schedule.uuid = this.store.createId();
        schedule.createdBy = user.email;
        schedule.createdByuserId = user.uuid;
        schedule.createdAt = firebase.default.firestore.Timestamp.now();
        return this.store.collection<any>('schedules').doc(schedule.uuid).set(schedule, { merge: true });
      })
    );
  }

  public updateSchedules(schedule: any) {
    schedule['updatedAt'] = firebase.default.firestore.Timestamp.now();
    return of(this.store.collection<any>('schedules').doc(schedule.uuid).update(schedule));
  }

  public addAdminRoles(access: any) {
    return this.authService.getUser().pipe(
      switchMap((user: UserDetail) => {
        access.uuid = this.store.createId();
        access.createdBy = user.email;
        access.createdByuserId = user.uuid;
        access.createdAt = firebase.default.firestore.Timestamp.now();
        return this.store.collection<any>('admin-roles').doc(access.uuid).set(access, { merge: true });
      })
    );
  }

  public updateAdminRole(access: any) {
    return this.afAuth.authState.pipe(
      switchMap((user: firebase.default.User) => {
        access['updatedAt'] = firebase.default.firestore.Timestamp.now();
        return this.store.collection<Survey>('admin-roles').doc(access.uuid).update(access);
      })
    );
  }

  public async deleteAccess(accessUUID: string) {
    return this.store.collection('admin-roles').doc(accessUUID).delete();
  }

  public async deleteSchedule(uuid: string) {
    return this.store.collection('schedules').doc(uuid).delete();
  }

  public getSurveyUUID(surveyUUID: any) {
    localStorage.removeItem('surveyUUID');
    localStorage.setItem('surveyUUID', surveyUUID);
  }

  public getCognitiveUUID(cognitiveUUID: any) {
    localStorage.removeItem('cognitiveUUID');
    localStorage.setItem('cognitiveUUID', cognitiveUUID);
  }

  public async deleteSurvey(surveyUUID: string) {
    const qry = await this.store.collection('surveys').doc(surveyUUID).collection('questions').ref.get();
    qry.forEach((doc: any) => {
      doc.ref.delete();
    });
    return this.store.collection('surveys').doc(surveyUUID).delete();
  }

  public async deleteCognitive(cognitiveUUID: string) {
    return this.store.collection('cognitives').doc(cognitiveUUID).delete();
  }

  // Activities
  public getDefaultActivityUUID(defaultActivityUUID: any) {
    localStorage.removeItem('defaultActivityUUID');
    localStorage.setItem('defaultActivityUUID', defaultActivityUUID);
  }

  public addDefaultActivity(defaultActivity: DefaultActivity) {
    return this.store.collection<DefaultActivity>('default-activities').doc(defaultActivity.uuid).set(defaultActivity);
  }

  public getDefaultActivities() {
    return this.store.collection<DefaultActivity>('default-activities').valueChanges();
  }

  public getOneDefaultActivity() {
    return this.store
      .collection<DefaultActivity>('default-activities')
      .doc(localStorage.getItem('defaultActivityUUID'))
      .valueChanges();
  }

  public deleteDefaultActivity(uuid: string) {
    return this.store.collection<DefaultActivity>('default-activities').doc(uuid).delete();
  }

  // Store user in db
  public SetUserData(user: any) {
    return this.store.collection('user').doc(user.uid).set({
      email: user.email,
    });
  }

  public cloneSurveyQuestion(surveyUUID: string, question: SurveyQuestion) {
    question.qtext = `Clone ${question.qtext}`;
    question.uuid = this.store.createId();
    return this.store
      .collection('surveys')
      .doc(surveyUUID)
      .collection<SurveyQuestion>('questions')
      .doc(question.uuid)
      .set(question);
  }

  public getAllUsers(startKey: string, endKey: string): Observable<any> {
    return this.store
      .collection('user', (ref) => {
        return ref.orderBy('email').startAt(startKey).endAt(endKey);
      })
      .valueChanges();
  }

  public getUsers(): Observable<any> {
    return this.store.collection('user').valueChanges();
  }

  public getEnrolledUsers(): Observable<any> {
    return this.afAuth.authState.pipe(
      switchMap((user: firebase.default.User) => {
        if (user) {
          return this.store
            .collection('invite-enrolled-users', (ref) => {
              let query: Query = ref;
              return query.where('studyuuid', '==', this.studiesService.getStudyId());
            })
            .valueChanges();
        } else {
          return of([]);
        }
      })
    );
  }

  public inviteUser(invitePayLoad: InviteUserModel): Observable<any> {
    return this.afAuth.authState.pipe(
      switchMap((user: firebase.default.User) => {
        if (user) {
          invitePayLoad.uuid = this.store.createId();
          this.store
            .collection('invite-enrolled-users')
            .doc(invitePayLoad.uuid)
            .set(invitePayLoad)
            .then(() => {
              if (invitePayLoad.userRef !== null) {
                this.updateUserErollment(invitePayLoad);
              }
            });
          return from(invitePayLoad.uuid);
        } else {
          of(null);
        }
      }),
      take(1)
    );
  }

  public getUserRef(uid: string): firebase.default.firestore.DocumentReference {
    return this.store.collection('user').doc(uid).ref;
  }

  private updateUserErollment(invitePayLoad: InviteUserModel): void {
    let enrollinStudy = {} as StudyEnroll;
    enrollinStudy.createdAt = invitePayLoad.createdAt;
    enrollinStudy.studyId = invitePayLoad.studyuuid;
    enrollinStudy.studyName = invitePayLoad.studyName;
    enrollinStudy.studyRef = invitePayLoad.studyRef;
    enrollinStudy.termsCondition = invitePayLoad.termsCondition;
    enrollinStudy.isEnrolled = false;
    this.store
      .collection('user')
      .doc(invitePayLoad.uuid)
      .collection('enrollStudy')
      .doc(enrollinStudy.studyId)
      .set(enrollinStudy);
  }

  public unEnrollUserFromStudy(payLoad: any): Promise<any> {
    return Promise.all([
      this.store.doc(payLoad.userRef).collection('enrollStudy').doc(payLoad.studyId).update({
        isEnrolled: false,
        reasonForUnEnroll: payLoad.reasonForUnEnroll,
      }),
      this.store.collection('invite-enrolled-users').doc(payLoad.uuid).update({
        isEnrolled: false,
        reasonForUnEnroll: payLoad.reasonForUnEnroll,
      }),
    ]);
  }

  public getAllErolledUserInStudy(): Observable<any> {
    let studyId = this.studiesService.getStudyId();
    if (!studyId) {
      return of(null);
    }
    return this.store
      .collection('user-groups', (ref) => {
        let query: Query = ref;
        return query.where('isDelete', '==', false).where('isDefault', '==', true).where('studyId', '==', studyId);
      })
      .valueChanges()
      .pipe(map((items: UserGroupModel[]) => items.pop()));
  }

  public getMembers(): Observable<any> {
    return this.getAllErolledUserInStudy().pipe(
      mergeMap((group: UserGroupModel) => {
        if (!group) {
          return of([]);
        }
        return this.store
          .collection('group-members', (ref) => ref.where('groupId', '==', group.uuid))
          .valueChanges()
          .pipe(take(1));
      })
    );
  }

  public getDfaultStudyUsers(): Observable<any> {
    return this.afAuth.authState.pipe(
      switchMap((user: firebase.default.User) => {
        if (!user) {
          return of([]);
        }
        return this.store
          .collection('user', (ref) => {
            let query: Query = ref;
            return query;
          })
          .valueChanges()
          .pipe(take(1));
      })
    );
  }

  public getUserFcmToken(userId: string): Observable<any> {
    return this.store
      .collection('fcm-tokens', (ref) => {
        return ref.where('userId', '==', userId);
      })
      .valueChanges();
  }

  public getInvitedUsers(): Observable<any> {
    let studyId = this.studiesService.getStudyId();
    return this.store
      .collection('invite-enrolled-users', (ref) =>
        ref.where('studyuuid', '==', studyId).where('isEnrolled', '==', false).where('isInvited', '==', true)
      )
      .valueChanges();
  }
}
